import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverBody,
	PopoverArrow,
	IconButton,
} from "@chakra-ui/react";
import Button from "../../components/Core/Button/Button";
import Modal from "../../components/Core/Modal/Modal";
import { ConsultantSalary, SalaryByType } from "../../models/Salary";
import { routes } from "../../configs/constants/routes";
import { convertToSentenceCase, formatCurrencyString } from "../../utils/string.utils";
import { formatToMaxTwoDecimals } from "@/utils/number.utils";
import { translateWeekday } from "../../utils/date.time.utils";

interface ISalaryDetailsModalProps {
	consultant: ConsultantSalary;
	onClose: () => any;
	periodFrom?: Date;
	periodTo?: Date;
	contractId?: string;
}

const SalaryDetailsModal: React.FC<ISalaryDetailsModalProps> = ({
	consultant,
	onClose,
	periodFrom,
	periodTo,
	contractId,
}) => {
	const { t } = useTranslation();
	const {
		salary: { salaryTypes },
		profile: { employment },
	} = consultant;

	const currency = employment?.salary[0].currency || "SEK";

	// Build time reports URL with consultant and period filters
	// Format matches TimeReportsPage expectations: start/end dates and consultant as JSON object
	const consultantParam = encodeURIComponent(
		JSON.stringify({
			key: consultant.id,
			value: `${consultant.firstName} ${consultant.lastName}`,
		})
	);
	const timeReportsUrl =
		periodFrom && periodTo
			? `${routes.TIME_REPORTS}?start=${moment(periodFrom).format("YYYY-MM-DD")}&end=${moment(
					periodTo
			  ).format("YYYY-MM-DD")}&consultant=${consultantParam}`
			: `${routes.TIME_REPORTS}?consultant=${consultantParam}`;

	return (
		<Modal maxWidth="700px" onClose={onClose}>
			<div className="flex flex-col gap-1 mb-4 items-center">
				<div className="text-lg font-bold">{t("SALARY_BREAKDOWN")}</div>

				<div className="text-gray-500 flex gap-2 items-center">
					<a
						href={`/consultants/${consultant.id}/profile`}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm text-cyan-600 hover:text-blue-800 hover:underline"
					>
						{consultant.firstName} {consultant.lastName}
					</a>

					<a href={timeReportsUrl} target="_blank" rel="noopener noreferrer" className="text-sm ">
						(
						<span className="text-cyan-600 hover:text-blue-800 hover:underline">
							{t("VIEW_TIME_REPORTS")}
						</span>
						)
					</a>
				</div>
			</div>

			<div className="flex flex-col gap-2 w-full max-h-[55vh] overflow-y-scroll">
				<Row
					title={t("salary.breakdown.hourly")}
					salaryByType={salaryTypes.hourly}
					currency={currency}
					contractId={contractId}
				/>

				{/* OB supplements */}
				{salaryTypes.ob1 && salaryTypes.ob1.total > 0 && (
					<Row
						title={t("salary.journal.salaryTypes.ob1")}
						salaryByType={salaryTypes.ob1}
						currency={currency}
						contractId={contractId}
					/>
				)}
				{salaryTypes.ob2 && salaryTypes.ob2.total > 0 && (
					<Row
						title={t("salary.journal.salaryTypes.ob2")}
						salaryByType={salaryTypes.ob2}
						currency={currency}
						contractId={contractId}
					/>
				)}
				{salaryTypes.ob3 && salaryTypes.ob3.total > 0 && (
					<Row
						title={t("salary.journal.salaryTypes.ob3")}
						salaryByType={salaryTypes.ob3}
						currency={currency}
						contractId={contractId}
					/>
				)}
				{salaryTypes.ob4 && salaryTypes.ob4.total > 0 && (
					<Row
						title={t("salary.journal.salaryTypes.ob4")}
						salaryByType={salaryTypes.ob4}
						currency={currency}
						contractId={contractId}
					/>
				)}
				{salaryTypes.ob5 && salaryTypes.ob5.total > 0 && (
					<Row
						title={t("salary.journal.salaryTypes.ob5")}
						salaryByType={salaryTypes.ob5}
						currency={currency}
						contractId={contractId}
					/>
				)}

				{salaryTypes.holidayPay && (
					<Row
						title={t("salary.breakdown.holidayPay")}
						salaryByType={salaryTypes.holidayPay}
						currency={currency}
						contractId={contractId}
					/>
				)}
				<Row
					title={t("salary.breakdown.childCare")}
					salaryByType={salaryTypes.childCare}
					currency={currency}
					contractId={contractId}
				/>
				<Row
					title={t("salary.breakdown.sickPay")}
					salaryByType={salaryTypes.sickPay}
					currency={currency}
					contractId={contractId}
				/>
				<Row
					title={t("salary.breakdown.sickPayNonPaid")}
					salaryByType={salaryTypes.sickPayNonPaid}
					currency={currency}
					contractId={contractId}
				/>
				<Row
					title={t("salary.breakdown.qualifyingDeduction")}
					salaryByType={{
						...salaryTypes.qualifyingDeduction,
						total: -salaryTypes.qualifyingDeduction.total,
					}}
					currency={currency}
					contractId={contractId}
				/>
				<Row
					title={t("salary.breakdown.leaveOfAbsence")}
					salaryByType={salaryTypes.leaveOfAbsence}
					currency={currency}
					contractId={contractId}
				/>
			</div>

			<Button onClick={onClose} margin="2rem 0 0 0" width="100%">
				{t("CLOSE")}
			</Button>
		</Modal>
	);
};

const Row: React.FC<{
	title: string;
	salaryByType: SalaryByType;
	currency: string;
	contractId?: string;
}> = ({ title, salaryByType, currency, contractId }) => {
	const [isExpanded, setExpanded] = useState(false);

	return (
		<div
			onClick={() => setExpanded(!isExpanded)}
			className={`w-full p-4 border border-gray-300 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:shadow-sm ${
				isExpanded ? "bg-gray-50" : "bg-white"
			}`}
		>
			<div className="flex justify-between items-center">
				<label className="font-medium text-[0.95rem] text-gray-700">{title}</label>
				<span className="font-semibold text-base text-black">
					{formatCurrencyString(salaryByType.total, { currency })}
				</span>
			</div>
			{isExpanded && (
				<div className="mt-4 pt-4 border-t border-gray-300 flex flex-col gap-2">
					{salaryByType.entries.map((entry, i) => (
						<div key={i} className="flex items-center justify-between text-sm text-gray-700">
							<span className="font-medium">{moment(entry.date).format("YYYY-MM-DD")}</span>
							<div className="flex items-center gap-2">
								<span className="text-gray-500">
									{formatToMaxTwoDecimals(entry.quantity)} ×{" "}
									{formatCurrencyString(entry.price, { currency })}
								</span>

								{/* Info icon with popover for rationale */}
								{entry.rationale && (
									<Popover placement="left" trigger="click">
										<PopoverTrigger>
											<IconButton
												aria-label="Visa detaljer"
												icon={<InfoOutlineIcon />}
												size="xs"
												variant="ghost"
												colorScheme="blue"
												onClick={(e) => e.stopPropagation()}
											/>
										</PopoverTrigger>
										<PopoverContent onClick={(e) => e.stopPropagation()}>
											<PopoverArrow />
											<PopoverBody>
												<div className="flex flex-col gap-2 text-xs">
													{/* OB Rule rationale */}
													{entry.rationale.obRule && (
														<div className="flex flex-col gap-1">
															<div className="flex items-center gap-2">
																<span className="font-semibold">
																	{entry.rationale.obRule.name ||
																		`OB-regel ${entry.rationale.obRule.id.slice(0, 8)}`}
																</span>
																{(entry.rationale.obRule.contractId || contractId) && (
																	<a
																		href={`/contracts/${
																			entry.rationale.obRule.contractId || contractId
																		}/ob#${entry.rationale.obRule.id}`}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="text-xs text-cyan-600 hover:text-blue-800 hover:underline"
																		onClick={(e) => e.stopPropagation()}
																	>
																		Visa regel
																	</a>
																)}
															</div>
															<span className="text-gray-600">
																{entry.rationale.obRule.matchReason === "weekday" &&
																	entry.rationale.obRule.weekday &&
																	convertToSentenceCase(
																		translateWeekday(entry.rationale.obRule.weekday)
																	)}
																{entry.rationale.obRule.matchReason === "special_date" &&
																	"Särskilt datum"}
																{entry.rationale.obRule.matchReason === "time_overlap" &&
																	entry.rationale.obRule.timeRange}
															</span>
															<span className="text-gray-600">
																Tillägg: +{entry.rationale.obRule.supplement} SEK
															</span>
														</div>
													)}

													{/* GFL Rule rationale */}
													{entry.rationale.gflRule && (
														<div className="flex flex-col gap-1">
															<span className="font-semibold">GFL-lön</span>
															<span className="text-gray-600">
																{entry.rationale.gflRule.reason}
															</span>
															<span className="text-gray-600">
																Lön:{" "}
																{formatCurrencyString(entry.rationale.gflRule.rate, { currency })}
															</span>
														</div>
													)}

													{/* Calculation note rationale - always show if present */}
													{entry.rationale.calculationNote && (
														<div className="flex flex-col gap-1">
															<span className="text-gray-600">
																{entry.rationale.calculationNote}
															</span>
														</div>
													)}
												</div>
											</PopoverBody>
										</PopoverContent>
									</Popover>
								)}
							</div>
						</div>
					))}
					{salaryByType.entries.length === 0 && <span className="text-gray-500 italic">-</span>}
				</div>
			)}
		</div>
	);
};

export default SalaryDetailsModal;
